import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { lockScroll, unlockScroll } from "@/lib/scrollLock";
import { useLang } from "@/i18n";

const Lightbox = ({ images, labels, startIndex, open, onClose, origin }) => {
  const { t } = useLang();
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const touchStartX = useRef(0);
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (open) setIndex(startIndex);
  }, [open, startIndex]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocusRef.current = document.activeElement;
      dialog.showModal();
    }
    if (!open && dialog.open) {
      dialog.close();
      // Safari doesn't restore focus to the opener on its own.
      returnFocusRef.current?.focus?.();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [open]);

  const next = () => setIndex((i) => (i + 1) % images.length);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e) => {
      e.preventDefault();
      onClose();
    };
    const handleClick = (e) => {
      if (e.target === dialog) onClose();
    };
    const handleKey = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
    };
    dialog.addEventListener("cancel", handleCancel);
    dialog.addEventListener("click", handleClick);
    dialog.addEventListener("keydown", handleKey);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      dialog.removeEventListener("click", handleClick);
      dialog.removeEventListener("keydown", handleKey);
    };
  }, [onClose, images.length]);

  return (
    <dialog
      ref={dialogRef}
      data-testid="unit-lightbox"
      className="lb backdrop:bg-ink/90 bg-transparent p-0 m-auto w-screen h-screen max-w-none max-h-none"
      style={origin ? { "--lb-ox": `${origin.x}px`, "--lb-oy": `${origin.y}px` } : undefined}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) < 40) return;
        if (delta < 0) next(); else prev();
      }}
    >
      <div className="lb-stage relative w-full h-full flex items-center justify-center p-4 md:p-10">
        <button
          type="button"
          data-testid="unit-lightbox-close"
          onClick={onClose}
          aria-label={t.gallery.close}
          className="fixed top-4 right-4 md:top-6 md:right-6 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-ink/60 text-cream hover:bg-terracotta transition-colors duration-200"
        >
          <X className="h-5 w-5" />
        </button>

        {images.length > 1 && (
          <>
            <button
              type="button"
              data-testid="unit-lightbox-prev"
              onClick={prev}
              aria-label={t.gallery.prev}
              className="fixed left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-ink/60 text-cream hover:bg-terracotta transition-colors duration-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="unit-lightbox-next"
              onClick={next}
              aria-label={t.gallery.next}
              className="fixed right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-ink/60 text-cream hover:bg-terracotta transition-colors duration-200"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {images[index] && (
          <img
            key={index}
            src={images[index]}
            alt={labels?.[index] || ""}
            data-testid="unit-lightbox-image"
            className="lb-img max-w-full max-h-[78vh] w-auto h-auto object-contain rounded-2xl border border-cream/10 shadow-2xl select-none"
          />
        )}

        {images.length > 1 && (
          <p className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/80 text-sm font-medium" data-testid="unit-lightbox-counter">
            {index + 1} / {images.length}
          </p>
        )}
      </div>
    </dialog>
  );
};

export default Lightbox;
